import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, euro, API } from "@/lib/api";

function ServiceCard({ svc, cats, onSaved }) {
  const [form, setForm] = useState(svc);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(svc);

  const upload = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, image: `${API}/files/${data.path}` }));
      toast.success("Foto caricata — ricordati di salvare");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore caricamento"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/services/${svc.id}`, {
        name: form.name, description: form.description, base_price: Number(form.base_price),
        duration_min: Number(form.duration_min), image: form.image, category_id: form.category_id,
        from_price: !!form.from_price, active: !!form.active, for_whom: form.for_whom,
        non_derogable: !!form.non_derogable,
      });
      toast.success("Servizio aggiornato");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const inp = "w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso text-espresso text-sm";

  return (
    <div className={`border rounded-sm overflow-hidden ${form.active ? "border-espresso/10 bg-offwhite" : "border-espresso/10 bg-espresso/5 opacity-70"}`}>
      <div className="hover-zoom relative">
        <img src={form.image} alt={form.name} className="w-full aspect-[4/3] object-cover" />
        <label className="absolute bottom-3 right-3 cursor-pointer bg-espresso/90 text-ivory text-xs px-3 py-2 rounded-sm hover:bg-burgundy transition-colors">
          {uploading ? "Caricamento…" : "Cambia foto"}
          <input type="file" accept="image/*" data-testid={`svc-upload-${svc.id}`} className="hidden" onChange={(e) => upload(e.target.files)} />
        </label>
      </div>
      <div className="p-5 space-y-3">
        <input className={inp + " font-serif text-lg"} data-testid={`svc-name-${svc.id}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <select className={inp} data-testid={`svc-cat-${svc.id}`} value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
          {cats.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
        </select>
        <textarea className={inp + " resize-none"} rows={3} data-testid={`svc-desc-${svc.id}`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="flex gap-3">
          <div className="flex-1"><label className="kima-overline text-taupe">Prezzo €</label><input type="number" className={inp} data-testid={`svc-price-${svc.id}`} value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} /></div>
          <div className="flex-1"><label className="kima-overline text-taupe">Durata (min)</label><input type="number" className={inp} data-testid={`svc-duration-${svc.id}`} value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} /></div>
        </div>
        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 text-espresso/70"><input type="checkbox" checked={!!form.from_price} onChange={(e) => setForm({ ...form, from_price: e.target.checked })} /> "a partire da"</label>
          <label className="flex items-center gap-2 text-espresso/70"><input type="checkbox" data-testid={`svc-active-${svc.id}`} checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Attivo</label>
        </div>
        <label className="flex items-center gap-2 text-sm text-espresso/70"><input type="checkbox" data-testid={`svc-nonderog-${svc.id}`} checked={!!form.non_derogable} onChange={(e) => setForm({ ...form, non_derogable: e.target.checked })} /> Acconto non derogabile (sempre richiesto)</label>
        <button onClick={save} disabled={!dirty || saving} data-testid={`svc-save-${svc.id}`} className="w-full bg-espresso text-ivory py-2.5 rounded-sm hover:bg-burgundy transition-colors text-sm disabled:opacity-40">{saving ? "Salvataggio…" : dirty ? "Salva modifiche" : "Salvato"}</button>
      </div>
    </div>
  );
}

export default function ServicesManager() {
  const [services, setServices] = useState([]);
  const [cats, setCats] = useState([]);
  const [creating, setCreating] = useState(false);
  const [nw, setNw] = useState({ name: "", category_id: "essential", base_price: 30, duration_min: 60, description: "" });

  const load = () => {
    api.get("/admin/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/service-categories").then((r) => setCats(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const create = async () => {
    if (!nw.name) { toast.error("Inserisci il nome del servizio"); return; }
    try {
      await api.post("/admin/services", { ...nw, base_price: Number(nw.base_price), duration_min: Number(nw.duration_min) });
      toast.success("Servizio creato");
      setNw({ name: "", category_id: "essential", base_price: 30, duration_min: 60, description: "" });
      setCreating(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-espresso/60">Modifica foto, descrizione, prezzo e durata di ogni servizio. Ricordati di salvare ogni scheda.</p>
        <button onClick={() => setCreating(!creating)} data-testid="svc-toggle-create" className="bg-espresso text-ivory px-5 py-2 rounded-sm hover:bg-burgundy transition-colors text-sm">{creating ? "Chiudi" : "+ Nuovo servizio"}</button>
      </div>

      {creating && (
        <div className="border border-espresso/10 bg-offwhite rounded-sm p-6 mb-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-up">
          <input className="border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso" placeholder="Nome servizio" data-testid="svc-new-name" value={nw.name} onChange={(e) => setNw({ ...nw, name: e.target.value })} />
          <select className="border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso" data-testid="svc-new-cat" value={nw.category_id} onChange={(e) => setNw({ ...nw, category_id: e.target.value })}>
            {cats.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
          <input type="number" className="border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso" placeholder="Prezzo €" data-testid="svc-new-price" value={nw.base_price} onChange={(e) => setNw({ ...nw, base_price: e.target.value })} />
          <input type="number" className="border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso" placeholder="Durata min" data-testid="svc-new-duration" value={nw.duration_min} onChange={(e) => setNw({ ...nw, duration_min: e.target.value })} />
          <input className="sm:col-span-2 lg:col-span-3 border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso" placeholder="Descrizione" data-testid="svc-new-desc" value={nw.description} onChange={(e) => setNw({ ...nw, description: e.target.value })} />
          <button onClick={create} data-testid="svc-new-submit" className="bg-espresso text-ivory py-2 rounded-sm hover:bg-burgundy transition-colors text-sm">Crea servizio</button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {services.map((s) => (<ServiceCard key={s.id} svc={s} cats={cats} onSaved={load} />))}
      </div>
    </div>
  );
}
